// SAD-894: a deliverable's CLIN from its dutyKey (D107 family), resolved
// against the program's real lines. Never invent a number that isn't on file.
/** The fields of a contract line this helper reads (ListContractCLINs). */ /** A line priced on the base or an option period (never an optional-task CLIN). */ export function isPeriodClin(c) {
    const t = (c.optionType ?? '').toLowerCase();
    return t === 'base' || t === 'option_period';
}
/** Uppercased clinNumber → trimmed original, for on-file lookups. */ function clinIndex(clins) {
    const m = new Map();
    for (const c of clins){
        const raw = (c.clinNumber ?? '').trim();
        if (!raw) continue;
        const u = raw.toUpperCase();
        if (!m.has(u)) m.set(u, raw);
    }
    return m;
}
/** The on-file spelling of a candidate CLIN, or "" when it is not on file. */ export function clinOnFile(candidate, clins) {
    const c = (candidate ?? '').trim();
    if (!c) return '';
    return clinIndex(clins).get(c.toUpperCase()) ?? '';
}
/**
 * Duty keys are one of four shapes (D107):
 * - family: `x001AE` / `x0001AA` (same line in every period)
 * - single: `6001AU`, `10001AA`, `0101` (one concrete CLIN)
 * - non-CLIN: `pws:5.2:A`, `pws-5.2-A`, or any other manual / imported key
 */ export function classifyDutyKey(dutyKey) {
    const k = (dutyKey ?? '').trim();
    if (!k) return {
        kind: 'none',
        key: ''
    };
    if (/^pws[:-]/i.test(k)) return {
        kind: 'none',
        key: k
    };
    if (/^x\d+[A-Za-z]*$/i.test(k)) return {
        kind: 'family',
        key: `x${k.slice(1).toUpperCase()}`
    };
    if (/^\d+[A-Za-z]*$/i.test(k)) return {
        kind: 'single',
        key: k.toUpperCase()
    };
    return {
        kind: 'none',
        key: k
    };
}
/**
 * CLIN-shaped tokens embedded in an imported key (e.g. `CAATS-5001AH-DEL-1-01`
 * → `5001AH`). At least three digits so short counter segments (`1`, `01`) are
 * not treated as CLINs. Longest tokens first.
 */ export function embeddedClinTokens(dutyKey) {
    const matches = (dutyKey ?? '').toUpperCase().match(/\d{3,}[A-Z]{0,3}/g) ?? [];
    const seen = new Set();
    const out = [];
    for (const t of matches){
        if (seen.has(t)) continue;
        seen.add(t);
        out.push(t);
    }
    return out.sort((a, b)=>b.length - a.length || a.localeCompare(b));
}
/**
 * The first embedded CLIN token that is actually on file for the program, or
 * "" when none match (imported keys never invent a CLIN). PWS and hand-added
 * (`manual:`) keys are never scanned — a PWS section like `pws:1001` is not a CLIN.
 */ export function embeddedClinOnFile(dutyKey, clins) {
    const k = (dutyKey ?? '').trim();
    if (!k || /^pws[:-]/i.test(k) || /^manual:/i.test(k)) return '';
    const index = clinIndex(clins);
    for (const t of embeddedClinTokens(k)){
        const hit = index.get(t);
        if (hit) return hit;
    }
    return '';
}
/**
 * Real period CLINs on file for a family key (`x001AE` → 0001AE, 1001AE, …).
 * Only base / option_period lines whose leading digit is the period index —
 * never optional-task CLINs that share a suffix (5001AE, 6001AE, …).
 */ export function familyMembers(familyKey, clins) {
    const classified = classifyDutyKey(familyKey);
    if (classified.kind !== 'family') return [];
    const rest = classified.key.slice(1);
    const seen = new Set();
    const out = [];
    for (const c of clins){
        if (!isPeriodClin(c)) continue;
        const n = (c.clinNumber ?? '').trim().toUpperCase();
        if (n.length !== rest.length + 1) continue;
        if (n.slice(1) !== rest) continue;
        if (!/^\d/.test(n)) continue;
        if (seen.has(n)) continue;
        seen.add(n);
        out.push(n);
    }
    return out.sort();
}
/** List / header reading of a duty's CLIN: "0001AE +4", a single CLIN, or "". */ export function dutyClinDisplay(dutyKey, clins) {
    const classified = classifyDutyKey(dutyKey);
    if (classified.kind === 'none') {
        const embedded = embeddedClinOnFile(dutyKey, clins);
        return {
            label: embedded
        };
    }
    if (classified.kind === 'single') return {
        label: classified.key
    };
    const members = familyMembers(classified.key, clins);
    if (members.length === 0) return {
        label: classified.key
    };
    if (members.length === 1) return {
        label: members[0]
    };
    return {
        label: `${members[0]} +${members.length - 1}`,
        tooltip: members.join(', ')
    };
}
/**
 * The line a date belongs to in a period (pangaea LineOf): base → 0…,
 * option period N → N…. Empty when the key is not a family.
 */ export function lineOfFamily(familyKey, optionNumber) {
    const classified = classifyDutyKey(familyKey);
    if (classified.kind !== 'family') return '';
    if (!Number.isInteger(optionNumber) || optionNumber < 0) return '';
    return `${optionNumber}${classified.key.slice(1)}`;
}
/**
 * The CLIN shown on one date row / in the date drawer: only the deliverable's
 * own (SAD-1311). A single key or an on-file embedded token wins, even under an
 * unrelated exercise line; a duty with none shows "" — the optional-task
 * exercise CLIN is the team's line, not the deliverable's.
 * A family resolves to the period's line only when that line is on file;
 * otherwise the family key is shown, never an invented number.
 */ export function dateClinDisplay(opts) {
    const classified = classifyDutyKey(opts.dutyKey);
    if (classified.kind === 'none') {
        const embedded = embeddedClinOnFile(opts.dutyKey, opts.clins);
        return {
            label: embedded
        };
    }
    if (classified.kind === 'single') return {
        label: classified.key
    };
    if (opts.optionNumber === undefined) return dutyClinDisplay(classified.key, opts.clins);
    const candidate = lineOfFamily(classified.key, opts.optionNumber);
    if (!candidate) return {
        label: classified.key
    };
    // Period dates resolve only against period lines, never optional-task CLINs.
    const onFile = clinOnFile(candidate, opts.clins.filter((c)=>isPeriodClin(c)));
    if (onFile) return {
        label: onFile
    };
    return {
        label: classified.key
    };
}
/**
 * One CLIN for the list column: the current period's line when on file, else
 * the family's base/first member (never "+N"), else the family key. Single and
 * imported keys read as dateClinDisplay does.
 */ export function currentClinLabel(dutyKey, clins, optionNumber) {
    const classified = classifyDutyKey(dutyKey);
    if (classified.kind !== 'family') return dateClinDisplay({
        dutyKey,
        clins
    }).label;
    if (optionNumber !== undefined) {
        const line = dateClinDisplay({
            dutyKey,
            optionNumber,
            clins
        }).label;
        if (line !== classified.key) return line;
    }
    return familyMembers(classified.key, clins)[0] ?? classified.key;
}
/**
 * Whether a search query matches this duty's CLIN identity: the dutyKey
 * itself, a single CLIN, any resolved family member (so "0001AE" finds
 * the `x001AE` deliverable), or an embedded on-file CLIN in an imported key.
 */ export function dutyKeyMatchesClinQuery(dutyKey, query, clins) {
    const q = query.trim().toLowerCase();
    if (!q) return false;
    if (dutyKey.toLowerCase().includes(q)) return true;
    const classified = classifyDutyKey(dutyKey);
    if (classified.kind === 'single') return classified.key.toLowerCase().includes(q);
    if (classified.kind === 'family') {
        if (classified.key.toLowerCase().includes(q)) return true;
        return familyMembers(classified.key, clins).some((m)=>m.toLowerCase().includes(q));
    }
    const embedded = embeddedClinOnFile(dutyKey, clins);
    return !!embedded && embedded.toLowerCase().includes(q);
}
